import * as React from "react";
import { MutableRefObject } from "react";
import { Fields, SelectionType } from "./select.typings";
import { isContainSubString, isStringEmpty } from "../../common/stringUtils";

export type UseSelectReducerType<T> = [
  {
    displayedText: string;
    searchTerm: string;
    items: T[];
    isDropdownOpen: boolean;
    showClearButton: boolean;
    hasFocus: boolean;
    inputRef: MutableRefObject<HTMLInputElement | null>;
    isReadonly: boolean;
  },
  {
    toggleDropdown: (isShown: boolean) => void;
    toggleFocus: (hasFocus: boolean) => void;
    clearSelection: () => void;
    search: (term: string) => void;
    selectOption: (value: T, isSelected: boolean, isReadOnly: boolean) => void;
    createTag: (text: string) => void;
  }
];

export function useSelect<T>(
  items: T[],
  idField: Fields<T>,
  labelField: Fields<T>,
  singleSelect: boolean,
  selectedItems: T[],
  isFilterMode: boolean,
  onChange?: (selected: SelectionType<T>) => void,
  readOnlyItems?: T[],
  onCreateTag?: (searchTerm: string) => void,
  showClearButton?: boolean,
  onTermChange?: (searchTerm: string) => void,
  onClose?: () => void,
  customSearchPredicate?: (element: T) => boolean,
  eraseSearchTermOnClick?: boolean
): UseSelectReducerType<T> {
  const inputRef = React.createRef<HTMLInputElement>();
  const [itemsState, setItemsState] = React.useState<T[]>([]);
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [displayedText, setDisplayedText] = React.useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = React.useState<boolean>(false);
  const [hasFocus, setHasFocus] = React.useState<boolean>(false);
  const [showClearButtonState, setShowClearButtonState] = React.useState<boolean>(showClearButton ?? true);

  const isReadonly = React.useMemo(() => {
    return !!(singleSelect && readOnlyItems && readOnlyItems?.length > 0);
  }, [readOnlyItems, singleSelect]);

  const defaultPredicate = (element: T): boolean => isContainSubString(element[`${labelField}`], searchTerm)
  const searchPredicate = customSearchPredicate || defaultPredicate


  React.useEffect(() => {
    const selectedItemsAreReadOnly =
      selectedItems &&
      selectedItems.length > 0 &&
      readOnlyItems &&
      readOnlyItems.length > 0 &&
      selectedItems.filter((e) => !readOnlyItems.find((r) => r[`${idField}`] === e[`${idField}`])).length === 0;
    setShowClearButtonState(showClearButton === true && !selectedItemsAreReadOnly);
  }, [selectedItems, showClearButton, readOnlyItems, idField]);

  React.useEffect(() => {
    const memoizeSelectedItems = selectedItems ?? [];
    const displayedText =
      memoizeSelectedItems.length === 1
        ? memoizeSelectedItems[0][`${labelField}`]
        : memoizeSelectedItems.length > 1
        ? `${memoizeSelectedItems.length} selected`
        : "";
    setDisplayedText(displayedText);
  }, [selectedItems, labelField]);

  React.useEffect(() => {
    const normalizedItems = items ?? [];
    if (isFilterMode) {
      const value = isStringEmpty(searchTerm)
        ? normalizedItems
        : normalizedItems.filter(searchPredicate);
      setItemsState(value);
    } else {
      setItemsState(normalizedItems);
    }
  }, [items, searchTerm, labelField, isFilterMode]);

  const closeDropdown = (): void => {
    setIsDropdownOpen(false);
    onClose?.();
  };

  const clearSelection = (): void => {
    const readOnlySelected = selectedItems?.filter((e) =>
      readOnlyItems?.find((r) => r[`${idField}`] === e[`${idField}`])
    );

    if (readOnlySelected?.length > 0) {
      const result = singleSelect ? [readOnlySelected[0]] : readOnlySelected;
      onChange?.(result);
      return;
    }
    onChange?.([]);
  };

  const selectOption = (value: T, isSelected: boolean, isReadOnly: boolean): void => {
    const memoizeSelectedItems = selectedItems ?? [];
    if (eraseSearchTermOnClick) {
      setSearchTerm("");
    }
    if (!hasFocus) {
      setHasFocus(true);
    }
    if (isReadOnly) {
      return;
    }

    if (
      singleSelect &&
      memoizeSelectedItems.filter((e) => readOnlyItems?.find((r) => r[`${idField}`] === e[`${idField}`])).length > 0
    ) {
      return;
    }

    const newSelection = isSelected
      ? memoizeSelectedItems.filter((item) => value[`${idField}`] !== item[`${idField}`])
      : singleSelect
      ? [value]
      : [...memoizeSelectedItems, value];

    if (singleSelect) {
      search();
      closeDropdown();
    }

    onChange?.(newSelection);
  };

  const createTag = (text: string): void => {
    onCreateTag?.(text);
    search();
    if (singleSelect) {
      closeDropdown();
    }
  };

  const toggleDropdown = (isOpen: boolean): void => {
    setIsDropdownOpen(isOpen);
    setHasFocus(isOpen);
    if (!isOpen && searchTerm.length > 0) {
      search(); // reset search
    }
  };

  const toggleFocus = (hasFocus: boolean): void => {
    if (hasFocus) {
      setHasFocus(true);
      setIsDropdownOpen(true);
    } else {
      setHasFocus(false);
    }
  };

  const search = (term?: string): void => {
    const searchTerm = term ?? "";
    setSearchTerm(searchTerm);
    onTermChange?.(searchTerm);
  };

  const state = React.useMemo(
    () => ({
      displayedText,
      searchTerm,
      items: itemsState,
      isDropdownOpen,
      hasFocus,
      inputRef,
      showClearButton: showClearButtonState,
      isReadonly,
    }),
    [displayedText, searchTerm, itemsState, isDropdownOpen, inputRef, hasFocus, showClearButtonState, isReadonly]
  );

  return [state, { toggleDropdown, toggleFocus, clearSelection, search, selectOption, createTag }];
}
